import { useEffect, useState } from "react";
import { Link } from "wouter";
import { ArrowDown, ArrowLeft, ArrowRight } from "lucide-react";
import { fileUrl } from "@/lib/format";
import { CtaButton } from "@/components/cta-button";
import type { CtaLink, Project } from "@shared/schema";

const STATUS_LABEL: Record<string, string> = {
  planning: "Tervezés alatt",
  construction: "Kivitelezés alatt",
  sales: "Értékesítés alatt",
  completed: "Befejezett",
};

export type HeroSliderProps = {
  /** Projects whose covers + meta become the rotating slides */
  projects: Project[];
  /** Used when a project has no coverImageKey OR there are zero projects */
  fallbackImage: string;
  /** Static intro slide shown FIRST, before the project carousel */
  intro?: {
    eyebrow: string;
    title: string;
    subtitle: string;
    /** Fallback route for CTA 1 when `primaryLink` is not configured. */
    primaryHref: string;
    primaryLabel: string;
    /** Admin-configured navigation target for CTA 1 (page/url/anchor). */
    primaryLink?: CtaLink;
    /** Fallback route for CTA 2 when `secondaryLink` is not configured. */
    secondaryHref: string;
    secondaryLabel: string;
    /** Admin-configured navigation target for CTA 2 (page/url/anchor). */
    secondaryLink?: CtaLink;
  };
  /** ms between auto-advances. Default 6000. Pass 0 to disable. */
  autoplayMs?: number;
};

type Slide =
  | { kind: "intro"; intro: NonNullable<HeroSliderProps["intro"]> }
  | { kind: "project"; project: Project };

/**
 * Full-screen hero with cross-fade carousel between projects.
 * - Auto-rotates every `autoplayMs` (paused on hover).
 * - Counter top-left, prev/next arrows on the side edges,
 *   slide-text overlay right-aligned (Go.arch style).
 * - Falls back gracefully when there are no projects.
 */
export function HeroSlider({
  projects,
  fallbackImage,
  intro,
  autoplayMs = 6000,
}: HeroSliderProps) {
  const slides: Slide[] = [];
  if (intro) slides.push({ kind: "intro", intro });
  for (const p of projects.slice(0, 4)) {
    slides.push({ kind: "project", project: p });
  }
  if (slides.length === 0 && intro) {
    slides.push({ kind: "intro", intro });
  }

  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || slides.length <= 1 || autoplayMs <= 0) return;
    const t = setInterval(() => {
      setActive((i) => (i + 1) % slides.length);
    }, autoplayMs);
    return () => clearInterval(t);
  }, [paused, slides.length, autoplayMs]);

  const next = () => setActive((i) => (i + 1) % slides.length);
  const prev = () =>
    setActive((i) => (i - 1 + slides.length) % slides.length);

  return (
    <section
      className="relative -mt-0 h-[100vh] min-h-[640px] overflow-hidden bg-brand-900"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* All slide backgrounds layered with cross-fade */}
      {slides.map((s, i) => (
        <SlideBackground
          key={slideKey(s, i)}
          slide={s}
          fallbackImage={fallbackImage}
          isActive={i === active}
        />
      ))}

      {/* Universal dark gradient overlay */}
      <div
        className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/35 to-black/65 pointer-events-none"
        aria-hidden
      />

      {/* Counter top-left */}
      {slides.length > 1 && (
        <div className="absolute top-24 lg:top-32 left-6 lg:left-12 z-20 flex items-baseline gap-2 text-white pointer-events-none">
          <span className="font-heading text-5xl lg:text-7xl text-accent leading-none">
            {String(active + 1).padStart(2, "0")}
          </span>
          <span className="font-heading text-base text-white/65">
            / {String(slides.length).padStart(2, "0")}
          </span>
        </div>
      )}

      {/* Prev/next arrows */}
      {slides.length > 1 && (
        <>
          <button
            type="button"
            onClick={prev}
            className="hidden md:flex absolute left-4 lg:left-12 top-1/2 -translate-y-1/2 z-20 items-center gap-2 text-white/70 hover:text-accent transition-colors text-xs uppercase tracking-widish font-semibold"
            aria-label="Előző"
          >
            <ArrowLeft size={20} />
            <span className="hidden lg:inline">Előző</span>
          </button>
          <button
            type="button"
            onClick={next}
            className="hidden md:flex absolute right-4 lg:right-12 top-1/2 -translate-y-1/2 z-20 items-center gap-2 text-white/70 hover:text-accent transition-colors text-xs uppercase tracking-widish font-semibold"
            aria-label="Következő"
          >
            <span className="hidden lg:inline">Következő</span>
            <ArrowRight size={20} />
          </button>
        </>
      )}

      {/* Active slide overlay text */}
      <div className="relative z-10 container h-full flex items-center pt-16 lg:pt-0">
        {slides.map((s, i) => (
          <SlideOverlay
            key={slideKey(s, i)}
            slide={s}
            isActive={i === active}
          />
        ))}
      </div>

      {/* Pagination dots — bottom-center on mobile (when arrows are hidden) */}
      {slides.length > 1 && (
        <div className="md:hidden absolute bottom-20 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setActive(i)}
              className={`w-2 h-2 rounded-full transition-all ${
                i === active ? "w-8 bg-accent" : "bg-white/40 hover:bg-white/70"
              }`}
              aria-label={`${i + 1}. slide`}
            />
          ))}
        </div>
      )}

      {/* Scroll indicator */}
      <a
        href="#about-preview"
        className="hidden lg:flex absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex-col items-center gap-2 text-white/65 hover:text-accent transition-colors animate-bounce"
      >
        <span className="text-[10px] uppercase tracking-widish font-semibold">
          Tovább
        </span>
        <ArrowDown size={16} />
      </a>
    </section>
  );
}

function SlideBackground({
  slide,
  fallbackImage,
  isActive,
}: {
  slide: Slide;
  fallbackImage: string;
  isActive: boolean;
}) {
  let src = fallbackImage;
  if (slide.kind === "project") {
    if (slide.project.coverImageKey) {
      src = fileUrl(slide.project.coverImageKey);
    } else if (slide.project.gallery?.[0]) {
      src = fileUrl(slide.project.gallery[0].key);
    }
  }

  return (
    <div
      className={`absolute inset-0 transition-opacity duration-[1500ms] ease-out ${
        isActive ? "opacity-100" : "opacity-0"
      }`}
      aria-hidden={!isActive}
    >
      <img
        src={src}
        alt=""
        className={`w-full h-full object-cover ${
          isActive ? "scale-105" : "scale-100"
        } transition-transform duration-[7000ms] ease-out`}
      />
    </div>
  );
}

function SlideOverlay({ slide, isActive }: { slide: Slide; isActive: boolean }) {
  return (
    <div
      className={`absolute inset-0 flex items-center pointer-events-none ${
        isActive ? "opacity-100" : "opacity-0"
      } transition-opacity duration-700 ease-out`}
      aria-hidden={!isActive}
    >
      <div className="container">
        {slide.kind === "intro" ? (
          <IntroOverlay intro={slide.intro} />
        ) : (
          <ProjectOverlay project={slide.project} />
        )}
      </div>
    </div>
  );
}

function IntroOverlay({ intro }: { intro: NonNullable<HeroSliderProps["intro"]> }) {
  return (
    <div className="max-w-3xl ml-auto text-right text-white pointer-events-auto">
      <div className="text-xs font-semibold uppercase tracking-widish text-accent mb-5">
        {intro.eyebrow}
      </div>
      <h1 className="text-3xl sm:text-5xl lg:text-6xl xl:text-7xl text-white text-shadow-strong">
        {intro.title}
      </h1>
      <p className="mt-8 max-w-xl ml-auto text-base lg:text-lg text-white/85 font-light leading-relaxed">
        {intro.subtitle}
      </p>
      <div className="mt-10 flex flex-wrap gap-3 justify-end">
        <CtaButton
          link={intro.primaryLink}
          fallbackHref={intro.primaryHref}
          className="btn-primary"
        >
          {intro.primaryLabel} <ArrowRight size={14} />
        </CtaButton>
        <CtaButton
          link={intro.secondaryLink}
          fallbackHref={intro.secondaryHref}
          className="btn-light"
        >
          {intro.secondaryLabel}
        </CtaButton>
      </div>
    </div>
  );
}

function ProjectOverlay({ project }: { project: Project }) {
  return (
    <div className="max-w-3xl ml-auto text-right text-white pointer-events-auto">
      <div className="text-xs font-semibold uppercase tracking-widish text-accent mb-5">
        {STATUS_LABEL[project.status] ?? "Aktuális projekt"}
      </div>
      <h1 className="text-3xl sm:text-5xl lg:text-6xl xl:text-7xl text-white text-shadow-strong leading-tight">
        {project.name}
      </h1>
      {project.shortDescription && (
        <p className="mt-8 max-w-xl ml-auto text-base lg:text-lg text-white/85 font-light leading-relaxed">
          {project.shortDescription}
        </p>
      )}
      <div className="mt-10 flex flex-wrap gap-3 justify-end">
        <Link
          href={`/projektek/${project.slug}`}
          className="btn-primary"
        >
          Megnézem <ArrowRight size={14} />
        </Link>
        <Link href="/projektek" className="btn-light">
          Összes projekt
        </Link>
      </div>
    </div>
  );
}

function slideKey(s: Slide, i: number): string {
  return s.kind === "intro" ? `intro-${i}` : `project-${s.project.id}`;
}
